import React, { useState } from "react";
import {
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Presentation,
  CheckCircle2,
  AlertCircle,
  HardDriveDownload,
  RotateCw,
  Wifi,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} GB`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} KB`;
  return `${n} B`;
}

/**
 * Floating Download Task Dock (Góc phải màn hình - Multi Download Manager)
 * Hiển thị tiến trình tải ngầm, %, tốc độ tải, thời gian dự kiến và đánh giá mạng
 */
const LessonDownloadDock = ({
  tasks = [],
  onDismissTask,
  onClearCompleted,
  onPresentLesson,
  onRetryTask,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  // Khi có task mới thêm vào, tự động mở dock nếu đang bị đóng
  React.useEffect(() => {
    if (tasks.length > 0) {
      setIsClosed(false);
    }
  }, [tasks.length]);

  if (tasks.length === 0 || isClosed) return null;

  const downloadingCount = tasks.filter((t) => t.status === "downloading").length;
  const completedCount = tasks.filter((t) => t.status === "completed").length;
  const hasDownloading = downloadingCount > 0;

  // Tính % trung bình của các task đang tải để hiển thị trên nút thu nhỏ
  const activeTasks = tasks.filter((t) => t.status === "downloading");
  const avgPercent =
    activeTasks.length > 0
      ? Math.round(
          activeTasks.reduce((acc, cur) => acc + (cur.percent || 0), 0) /
            activeTasks.length
        )
      : 100;

  return (
    <div className="fixed bottom-4 right-4 z-[210] max-w-[calc(100vw-2rem)] select-none">
      <AnimatePresence mode="wait">
        {isMinimized ? (
          /* Trạng thái thu nhỏ: Floating Pill nhỏ gọn */
          <motion.div
            key="minimized"
            initial={{ opacity: 0, scale: 0.85, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 15 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2.5 rounded-full border border-red-200 bg-white/95 px-4 py-2.5 shadow-2xl backdrop-blur-md hover:border-red-300"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsMinimized(false)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setIsMinimized(false)}
              className="flex items-center gap-2.5 cursor-pointer"
            >
              <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-red-800 text-white shadow-md shadow-red-300/50">
                {hasDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {downloadingCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-black text-white ring-2 ring-white">
                    {downloadingCount}
                  </span>
                )}
              </div>
              <div className="min-w-0 pr-1 text-left">
                <p className="text-xs font-bold text-gray-900 leading-tight">
                  {hasDownloading ? `Đang tải ${downloadingCount} bài (${avgPercent}%)` : "Tải xong tất cả"}
                </p>
                <p className="text-[10px] text-gray-500">
                  {hasDownloading ? "Bấm để xem chi tiết" : `${completedCount} bài sẵn sàng chiếu`}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(false);
                }}
                className="rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-700 transition"
                title="Mở rộng bảng tải"
              >
                <ChevronUp size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsClosed(true)}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
              title="Ẩn thông báo"
            >
              <X size={15} />
            </button>
          </motion.div>
        ) : (
          /* Trạng thái mở rộng: Floating Panel đầy đủ thông tin */
          <motion.div
            key="expanded"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.22 }}
            className="flex flex-col w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl border border-red-100 bg-white/95 shadow-2xl backdrop-blur-md"
          >
            {/* Header dock */}
            <div className="flex items-center justify-between border-b border-red-100 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-4 py-3 text-white">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/20 text-white backdrop-blur-sm">
                  <HardDriveDownload className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-xs font-black uppercase tracking-wider truncate">
                    Tác vụ tải bài giảng
                  </h4>
                  <p className="text-[10px] text-rose-100 truncate">
                    {hasDownloading
                      ? `Đang tải ${downloadingCount} bài giảng…`
                      : "Tất cả bài giảng đã hoàn tất"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {completedCount > 0 && (
                  <button
                    type="button"
                    onClick={onClearCompleted}
                    className="rounded-lg px-2 py-1 text-[10px] font-bold text-white/90 hover:bg-white/15 transition"
                    title="Xóa các bài đã tải xong khỏi danh sách"
                  >
                    Dọn xong
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsMinimized(true)}
                  className="rounded-lg p-1.5 text-white/80 hover:bg-white/15 hover:text-white transition"
                  title="Thu nhỏ xuống góc"
                >
                  <ChevronDown size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => setIsClosed(true)}
                  className="rounded-lg p-1.5 text-white/80 hover:bg-white/15 hover:text-white transition"
                  title="Đóng bảng tải"
                >
                  <X size={17} />
                </button>
              </div>
            </div>

            {/* Danh sách các tác vụ tải */}
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 p-2.5 space-y-2">
              {tasks.map((task) => {
                const isDone = task.status === "completed";
                const isErr = task.status === "error";
                const isDownloading = task.status === "downloading";

                return (
                  <div
                    key={task.id}
                    className={`rounded-2xl border p-3 transition-all ${
                      isDone
                        ? "border-emerald-100 bg-emerald-50/40"
                        : isErr
                          ? "border-red-200 bg-red-50/50"
                          : "border-gray-100 bg-white shadow-sm"
                    }`}
                  >
                    {/* Hàng trên: Icon + Tên bài học + Nút hành động */}
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="mt-0.5 shrink-0">
                          {isDownloading && (
                            <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-red-50 text-red-600">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          )}
                          {isDone && (
                            <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="h-4 w-4" />
                            </div>
                          )}
                          {isErr && (
                            <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-red-100 text-red-700">
                              <AlertCircle className="h-4 w-4" />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p
                            className="text-xs font-bold text-gray-900 truncate"
                            title={task.lessonTitle}
                          >
                            {task.lessonTitle}
                          </p>
                          <p
                            className="text-[10px] text-gray-500 truncate"
                            title={task.courseName}
                          >
                            {task.courseName} · {task.fileSize || formatBytes(task.total)}
                          </p>
                        </div>
                      </div>

                      {/* Hành động nhanh */}
                      <div className="flex items-center gap-1 shrink-0">
                        {isDone && onPresentLesson && (
                          <button
                            type="button"
                            onClick={() => onPresentLesson(task.lesson)}
                            className="inline-flex items-center gap-1 rounded-xl bg-gradient-to-r from-red-600 to-red-700 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white shadow-sm hover:from-red-500 hover:to-red-600 transition"
                            title="Mở trình chiếu ngay"
                          >
                            <Presentation size={13} />
                            Chiếu
                          </button>
                        )}
                        {isErr && onRetryTask && (
                          <button
                            type="button"
                            onClick={() => onRetryTask(task)}
                            className="inline-flex items-center gap-1 rounded-xl border border-red-200 bg-white px-2 py-1 text-[10px] font-bold text-red-700 hover:bg-red-50 transition"
                            title="Tải lại bài này"
                          >
                            <RotateCw size={12} />
                            Thử lại
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onDismissTask?.(task.id)}
                          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                          title="Bỏ khỏi danh sách"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Hàng giữa: Thanh Progress Bar */}
                    {isDownloading && (
                      <div className="mt-2.5 space-y-1.5">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-red-500 via-rose-500 to-red-600 transition-all duration-300 shadow-sm"
                            style={{
                              width: `${Math.min(100, Math.max(3, task.percent || 0))}%`,
                            }}
                          />
                        </div>

                        {/* Hàng dưới: Thông số chi tiết (%, dung lượng, tốc độ, thời gian dự kiến, mạng) */}
                        <div className="flex flex-wrap items-center justify-between gap-1 text-[10px] tabular-nums font-medium text-gray-600">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-bold text-red-700">
                              {task.percent || 0}%
                            </span>
                            <span className="text-gray-400">·</span>
                            <span className="text-gray-500">
                              {formatBytes(task.loaded)} / {formatBytes(task.total)}
                            </span>
                            <span className="text-gray-400">·</span>
                            <span className="font-semibold text-gray-700">
                              {task.speedFormatted || "0 KB/s"}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-gray-500">
                              {task.etaFormatted || "—"}
                            </span>
                            {task.networkLabel && (
                              <span
                                className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.2 text-[9px] font-bold ${task.networkBadgeClass}`}
                                title="Chất lượng đường truyền tải file"
                              >
                                <Wifi size={10} className="shrink-0" />
                                {task.networkLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Trạng thái Hoàn tất */}
                    {isDone && (
                      <div className="mt-1.5 flex items-center justify-between text-[10px]">
                        <span className="font-semibold text-emerald-800 flex items-center gap-1">
                          <Sparkles size={12} className="text-emerald-600" />
                          Đã lưu bài giảng vào máy · Sẵn sàng trình chiếu
                        </span>
                        <span className="text-emerald-700 font-mono">100%</span>
                      </div>
                    )}

                    {/* Trạng thái Lỗi */}
                    {isErr && (
                      <p className="mt-1 text-[10px] text-red-600 font-medium">
                        {task.errorMessage || "Không tải được bài giảng. Vui lòng thử lại."}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LessonDownloadDock;
